import { useState } from 'react';
import { api, errorMessage, getAuthNotice, setAuth } from '../lib/api';
import type { AuthMode } from '../lib/api';
import { ThemeToggle } from '../components/ThemeToggle';
import { Spinner } from '../components/Spinner';

/**
 * Man hinh dan key.
 *
 * Khong co dang nhap bang mat khau: capforge la dich vu tu host, thu xac thuc duy nhat
 * la API key co scope admin hoac CAPFORGE_ADMIN_TOKEN. Nguoi dung chon dung kieu vi hai
 * chuoi nay nhin ben ngoai khong phan biet duoc, ma server doc chung o hai header khac nhau.
 */
export function Login() {
  const [mode, setMode] = useState<AuthMode>('bearer');
  const [key, setKey] = useState('');
  const [reveal, setReveal] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Thong bao "bi day ra" chi doc mot lan luc dung component nay.
  const [notice] = useState<string | null>(() => getAuthNotice());

  const submit = (): void => {
    const trimmed = key.trim();
    if (trimmed.length === 0) {
      setError('Hãy dán key vào ô bên trên.');
      return;
    }
    setChecking(true);
    setError(null);
    api.verify({ key: trimmed, mode }).then(
      () => {
        // Chi luu sau khi server da xac nhan — tranh ket o vong 401 lien tuc.
        setAuth({ key: trimmed, mode });
      },
      (err: unknown) => {
        setChecking(false);
        setError(errorMessage(err));
      },
    );
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-slate-100 p-4 dark:bg-slate-950">
      <div className="w-full max-w-md">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold tracking-tight">capforge</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">bảng điều khiển</span>
          </div>
          <ThemeToggle />
        </div>

        <form
          className="cf-card p-5"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <h1 className="text-base font-semibold">Đăng nhập quản trị</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Dán một API key có scope <code className="cf-mono">admin</code>, hoặc giá trị{' '}
            <code className="cf-mono">CAPFORGE_ADMIN_TOKEN</code> trong tệp{' '}
            <code className="cf-mono">.env</code>.
          </p>

          {notice !== null && (
            <p className="mt-3 rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              {notice}
            </p>
          )}

          <div className="mt-4">
            <label className="cf-label" htmlFor="auth-mode">
              Kiểu xác thực
            </label>
            <select
              id="auth-mode"
              className="cf-select"
              value={mode}
              onChange={(event) => {
                setMode(event.target.value === 'adminToken' ? 'adminToken' : 'bearer');
              }}
            >
              <option value="bearer">API key scope admin — header Authorization: Bearer</option>
              <option value="adminToken">CAPFORGE_ADMIN_TOKEN — header X-Admin-Token</option>
            </select>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {mode === 'bearer'
                ? 'Key tạo từ trang API key, bắt đầu bằng cf_live_.'
                : 'Token đặt trong biến môi trường khi khởi động server.'}
            </p>
          </div>

          <div className="mt-4">
            <label className="cf-label" htmlFor="auth-key">
              Key / token
            </label>
            <div className="flex gap-2">
              <input
                id="auth-key"
                className="cf-input cf-mono"
                type={reveal ? 'text' : 'password'}
                autoComplete="off"
                spellCheck={false}
                placeholder={mode === 'bearer' ? 'cf_live_…' : 'admin token'}
                value={key}
                onChange={(event) => setKey(event.target.value)}
              />
              <button
                type="button"
                className="cf-btn cf-btn-ghost shrink-0"
                onClick={() => setReveal((v) => !v)}
              >
                {reveal ? 'Ẩn' : 'Hiện'}
              </button>
            </div>
          </div>

          {error !== null && (
            <p className="mt-3 rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
              {error}
            </p>
          )}

          <button type="submit" className="cf-btn cf-btn-primary mt-4 w-full" disabled={checking}>
            {checking ? <Spinner label="Đang kiểm tra" /> : 'Vào dashboard'}
          </button>

          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Key được lưu trong localStorage của trình duyệt này (khoá{' '}
            <code className="cf-mono">capforge.adminKey</code>). Dùng máy chung thì nhớ đăng xuất.
          </p>
        </form>
      </div>
    </div>
  );
}
